import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { TimerOff, AlertTriangle } from "lucide-react";

export const AUTO_LOCK_DEFAULTS = { cashier: 5, manager: 10, owner: 15 };
const OPTIONS = [[1, "1 minute (testing)"], [5, "5 minutes"], [10, "10 minutes"], [15, "15 minutes"], [30, "30 minutes"], [0, "Never / Disabled"]];

export default function AutoLockSettings() {
  const { business, loadBusiness } = useAuth();
  const [saving, setSaving] = useState(null);
  const current = { ...AUTO_LOCK_DEFAULTS, ...(business?.auto_lock_minutes || {}) };

  const save = async (role, val) => {
    const mins = parseInt(val);
    setSaving(role);
    try {
      await api.put("/business", { auto_lock_minutes: { ...current, [role]: mins } });
      await loadBusiness();
      toast.success(mins === 0 ? `Auto-lock disabled for ${role}s` : `${role[0].toUpperCase()}${role.slice(1)}s now auto-lock after ${mins} minute${mins === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const anyDisabled = Object.values(current).some((v) => v === 0);

  return (
    <Card className="p-6" data-testid="auto-lock-card">
      <p className="font-head font-bold flex items-center gap-2 mb-1"><TimerOff className="w-5 h-5" /> Auto-Lock After Inactivity</p>
      <p className="text-sm text-muted-foreground mb-4">NEEC will lock the screen after this period of inactivity. Open shifts remain active.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {Object.keys(AUTO_LOCK_DEFAULTS).map((role) => (
          <div key={role}>
            <Label className="text-xs capitalize">{role}</Label>
            <Select value={String(current[role])} onValueChange={(v) => save(role, v)} disabled={saving === role}>
              <SelectTrigger data-testid={`auto-lock-${role}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {OPTIONS.map(([v, l]) => <SelectItem key={v} value={String(v)} data-testid={`auto-lock-${role}-opt-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      {anyDisabled && (
        <p className="mt-3 text-xs text-amber-600 flex items-center gap-1.5" data-testid="auto-lock-disabled-warning">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Disabling auto-lock may leave business data visible when a user steps away.
        </p>
      )}
      <p className="text-[11px] text-muted-foreground mt-3">Changes save immediately and apply to trusted terminals on their next activity check.</p>
    </Card>
  );
}
